import { ColorRepresentation, CylinderGeometry, Material, Mesh, MeshBasicMaterial, Object3D, Vector3 } from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import {
  ARROW_HEAD_LENGTH_RATIO,
  ARROW_HEAD_RADIUS_RATIO,
  OVERLAY_RENDER_ORDER,
  OverlayLine,
  createOverlayConeGeometry,
  createOverlayLineMaterial,
  createOverlayMeshMaterial,
  setOverlayBaseColor
} from '../visualization/overlay-style';

const _axis = /*@__PURE__*/ new Vector3();
const _up = /*@__PURE__*/ new Vector3(0, 1, 0);
const _base = /*@__PURE__*/ new Vector3();
const _tip = /*@__PURE__*/ new Vector3();
const _chord = /*@__PURE__*/ new Vector3();

/** Moment arc radius as a share of the arrow length. */
const MOMENT_ARC_RADIUS_RATIO = 0.25;

/** Angle the moment symbol covers, arc plus head. */
const MOMENT_SWEEP = Math.PI * 1.5;

const MOMENT_ARC_SEGMENTS = 64;

/**
 * Point on the moment circle at angle `theta`, at `radius`. The sweep is
 * right-handed about local +Y, the axis setDirection puts on the moment vector.
 */
function momentArcPoint(theta: number, radius: number, target: Vector3): Vector3 {
  return target.set(-radius * Math.cos(theta), 0, radius * Math.sin(theta));
}

/**
 * Load arrow (shape 'line') or moment arc (shape 'circle'). Drawn length is
 * half the given length: the head tip sits at local y = length / 2.
 */
class ArrowHelper extends Object3D {
  _lineGeometry: LineGeometry;
  _coneGeometry: CylinderGeometry;
  _lineMaterial: LineMaterial;

  override type: string;
  line: OverlayLine;
  cone: Mesh;

  constructor(
    public shape: 'line' | 'circle' = 'line',
    dir = new Vector3(0, 0, 1),
    origin = new Vector3(0, 0, 0),
    length = 1,
    color: ColorRepresentation = 0xffff00,
    headLength = length * ARROW_HEAD_LENGTH_RATIO,
    headWidth = headLength * ARROW_HEAD_RADIUS_RATIO,
    isForAccelerationArrow = false
  ) {
    super();

    this.type = 'ArrowHelper';

    this._lineGeometry = new LineGeometry();
    this._lineMaterial = createOverlayLineMaterial(color);
    this._coneGeometry = createOverlayConeGeometry();

    if (shape === 'circle') {
      this.setArcSweep(MOMENT_SWEEP);
    } else {
      this._lineGeometry.setPositions([0, 0, 0, 0, 0.5, 0]);
      this._coneGeometry.translate(0, -0.5, 0);

      if (isForAccelerationArrow) {
        this._lineGeometry.translate(0, -0.25, 0);
        this._coneGeometry.translate(0, -1, 0);
      }
    }

    this.position.copy(origin);
    this.renderOrder = OVERLAY_RENDER_ORDER;

    this.line = new OverlayLine(this._lineGeometry, this._lineMaterial);
    this.line.matrixAutoUpdate = false;
    this.add(this.line);

    this.cone = new Mesh(this._coneGeometry, createOverlayMeshMaterial(color));
    this.cone.renderOrder = OVERLAY_RENDER_ORDER;
    this.cone.matrixAutoUpdate = false;
    this.add(this.cone);

    this.line.layers.set(1);
    this.cone.layers.set(1);

    this.setDirection(dir);
    this.setLength(length, headLength, headWidth);

    if (isForAccelerationArrow) {
      this.position.z = 1.5;
    }
  }

  /** Moment arc from angle 0 to `sweep`, on the unit circle the line scale sizes. */
  private setArcSweep(sweep: number): void {
    const vertices: number[] = [];
    const point = new Vector3();
    for (let s = 0; s <= MOMENT_ARC_SEGMENTS; s++) {
      momentArcPoint((s / MOMENT_ARC_SEGMENTS) * sweep, 0.5, point);
      vertices.push(point.x, point.y, point.z);
    }
    this._lineGeometry.setPositions(vertices);
  }

  setMaterials(lineMaterial: LineMaterial, coneMaterial: Material): void {
    this.line.material = lineMaterial;
    this.cone.material = coneMaterial;
  }

  setDirection(dir: Vector3): void {
    // dir is assumed to be normalized
    dir.normalize();

    if (dir.y > 0.99999) {
      this.quaternion.set(0, 0, 0, 1);
    } else if (dir.y < -0.99999) {
      this.quaternion.set(1, 0, 0, 0);
    } else {
      _axis.set(dir.z, 0, -dir.x).normalize();
      const radians = Math.acos(dir.y);
      this.quaternion.setFromAxisAngle(_axis, radians);
    }
  }

  setLength(length: number, headLength = length * ARROW_HEAD_LENGTH_RATIO, headWidth = headLength * ARROW_HEAD_RADIUS_RATIO): void {
    if (this.shape === 'circle') {
      // The arc stops one head length short of the sweep's end; the head's base
      // sits on that end and its tip lands back on the circle, so the head
      // follows the curve instead of leaving it along the end tangent.
      const radius = length * MOMENT_ARC_RADIUS_RATIO;
      const headAngle = radius > 0 ? Math.min(headLength / radius, MOMENT_SWEEP * 0.5) : 0;
      const arcEnd = MOMENT_SWEEP - headAngle;

      this.setArcSweep(arcEnd);
      this.line.scale.setScalar(length * 0.5);
      this.line.updateMatrix();

      momentArcPoint(arcEnd, radius, _base);
      momentArcPoint(MOMENT_SWEEP, radius, _tip);
      _chord.subVectors(_tip, _base);
      const chordLength = _chord.length();
      if (chordLength > 0) this.cone.quaternion.setFromUnitVectors(_up, _chord.divideScalar(chordLength));
      this.cone.position.addVectors(_base, _tip).multiplyScalar(0.5);
      this.cone.scale.set(headWidth, chordLength, headWidth);
      this.cone.updateMatrix();
    } else {
      this.line.scale.set(1, Math.max(0.0001, length - headLength), 1);
      this.line.updateMatrix();

      this.cone.scale.set(headWidth, headLength, headWidth);
      this.cone.position.y = length / 2;
      this.cone.updateMatrix();
    }
  }

  setColor(color: ColorRepresentation): void {
    if (this.line.material instanceof LineMaterial) setOverlayBaseColor(this.line.material, color);
    if (this.cone.material instanceof MeshBasicMaterial) setOverlayBaseColor(this.cone.material, color);
  }

  override copy(source: this): this {
    super.copy(source, false);

    this.line.copy(source.line);
    this.cone.copy(source.cone);

    return this;
  }

  override dispose(): void {
    this.line.geometry.dispose();
    this.line.material.dispose();
    this.cone.geometry.dispose();
    (this.cone.material as Material).dispose();
    super.dispose();
  }
}

export { ArrowHelper };
